import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Ban, CalendarPlus, Eye, Gift, KeyRound, Loader2, RotateCcw, Search, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, Field, Loader, PageHeader, StatusPill } from "@/components/common";

const VIOLET = "var(--glow-admin)";
const PLAN_RGB = { trial: "var(--glow-calendar)", lapsed: "var(--glow-media)", past_due: "var(--glow-media)", comped: "var(--glow-money)", active: "var(--glow-money)", canceling: "var(--glow-lists)", demo: "var(--glow-admin)" };
const PLAN_NAMES = { essentials: "Essentials", family_plus: "Family Plus", household_pro: "Household Pro", roommates: "Roommates" };
const TIERS = ["", "essentials", "family_plus", "household_pro", "roommates"];
const STATUSES = ["", "trial", "active", "canceling", "past_due", "lapsed", "comped", "gifted", "demo"];

function AiUsage() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/ai-usage").then(({ data }) => setD(data.days)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <section className="q-card mt-4" data-testid="admin-ai-usage">
      <h3 className="mb-2 font-display font-extrabold text-q-text">AI media today and recent days (UTC)</h3>
      <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-q-faint"><th>Date</th><th>Stories</th><th>Illustrations</th><th>Narrations</th><th>Lullabies</th></tr></thead>
        <tbody>{d.slice(0, 7).map((r) => <tr key={r.date} className="text-q-text" data-testid={`ai-usage-${r.date}`}><td>{r.date}</td><td>{r.stories}</td><td>{r.illustrations}</td><td>{r.narrations}</td><td>{r.lullabies}</td></tr>)}</tbody></table></div>
    </section>
  );
}

function BillingStats() {
  const [d, setD] = useState(null);
  const [price, setPrice] = useState("");
  const [yearly, setYearly] = useState("");
  const load = () => api.get("/admin/billing").then(({ data }) => { setD(data); setPrice((data.price_cents / 100).toFixed(2)); setYearly((data.yearly_cents / 100).toFixed(2)); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const save = async (e) => { e.preventDefault(); try { await api.put("/admin/billing/price", { amount_cents: Math.round(parseFloat(price) * 100), yearly_cents: Math.round(parseFloat(yearly) * 100) }); load(); } catch (x) { alert(errMsg(x)); } };
  return (
    <div className="mb-4 flex flex-wrap items-end gap-4 rounded-xl border border-q-border bg-q-surface p-4" data-testid="admin-billing-stats">
      <div><div className="text-[10px] font-black uppercase tracking-[0.16em] text-q-faint">Paying</div><div className="font-display text-2xl font-extrabold text-q-text" data-testid="admin-paying-count">{d.paying}</div></div>
      <div><div className="text-[10px] font-black uppercase tracking-[0.16em] text-q-faint">MRR</div><div className="font-display text-2xl font-extrabold text-q-text" data-testid="admin-mrr">${(d.mrr_cents / 100).toFixed(2)}</div></div>
      <div><div className="text-[10px] font-black uppercase tracking-[0.16em] text-q-faint">Gift revenue</div><div className="font-display text-2xl font-extrabold text-q-text" data-testid="admin-gift-revenue">${(d.gift_revenue_cents / 100).toFixed(2)} <span className="text-xs font-semibold text-q-muted">({d.gifts} gift{d.gifts === 1 ? "" : "s"})</span></div></div>
      <div className="flex flex-wrap gap-4" data-testid="admin-mrr-per-plan">
        {(d.per_plan || []).map((r) => <div key={r.plan} className="text-xs text-q-muted" data-testid={`admin-mrr-${r.plan}`}>{PLAN_NAMES[r.plan]}: <b className="text-q-text">{r.paying}</b> paying · <b className="text-q-text">${(r.mrr_cents / 100).toFixed(2)}</b> MRR</div>)}
        {d.founding && <div className="text-xs text-q-muted" data-testid="admin-founding-summary">Founding: {d.founding.claimed} claimed · {d.founding.in_trial} in trial · {d.founding.left} left</div>}
      </div>
      <Link to="/admin/plans" className="q-btn q-btn-secondary ml-auto" data-testid="admin-plans-link">Edit plans</Link>
    </div>
  );
}
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function useList(url, q) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get(url, { params: q !== undefined ? { q } : {} }).then(({ data }) => setRows(data)), [url, q]);
  useEffect(() => { load(); }, [load]);
  return [rows, load];
}

function SearchBox({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative mb-5 max-w-md">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-q-faint" />
      <input className="q-input pl-9" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function ActionDialog({ action, onClose, onDone }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [compPlan, setCompPlan] = useState("family_plus");
  if (!action) return null;
  const { kind, hub } = action;
  const cfg = {
    extend: { title: `Extend ${hub.name}'s trial`, label: "Days (1–90)", url: `/admin/organizations/${hub.id}/extend-trial`, body: { days: value }, cta: "Extend trial" },
    comp: { title: `Comp ${hub.name}`, label: "Free months (1–24)", url: `/admin/organizations/${hub.id}/comp`, body: { months: value, plan: compPlan }, cta: "Comp hub" },
    storage: { title: `Storage for ${hub.name}`, label: "Storage limit in GB (1–1000)", url: `/admin/organizations/${hub.id}/storage`, body: { limit_gb: Number(value) }, cta: "Save limit", put: true },
    delete: { title: `Delete ${hub.name}?`, label: "Type the hub's exact name to confirm", cta: "Delete hub forever" },
  }[kind];
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (kind === "delete") await api.delete(`/admin/organizations/${hub.id}`, { data: { confirm_name: value } });
      else if (cfg.put) await api.put(cfg.url, cfg.body);
      else await api.post(cfg.url, cfg.body);
      toast.success(kind === "delete" ? `${hub.name} deleted` : "Saved");
      onDone();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-q-line bg-q-surface sm:max-w-md" data-testid={`admin-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold">{cfg.title}</DialogTitle>
          <DialogDescription className="text-q-muted">
            {kind === "delete" ? "This permanently removes the hub, its kids, bills, spending and files. Its adults keep their other hubs." : kind === "storage" ? `Now: ${hub.storage?.label}.` : `Current plan: ${hub.plan.label}, ends ${fmt(hub.plan.ends_at)}.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {kind === "comp" && <label className="block text-sm text-q-muted">Plan
            <select className="q-input mt-1 w-full" value={compPlan} onChange={(e) => setCompPlan(e.target.value)} data-testid="admin-comp-plan">
              {["essentials", "family_plus", "household_pro"].map((p) => <option key={p} value={p}>{PLAN_NAMES[p]}</option>)}</select></label>}
          <Field label={cfg.label} error={error} errorTestid={`admin-${kind}-error`}>
            <input className="q-input" inputMode={kind === "delete" ? "text" : "numeric"} value={value} placeholder={kind === "delete" ? hub.name : ""}
              onChange={(e) => { setValue(e.target.value); setError(""); }} data-testid={`admin-${kind}-input`} />
          </Field>
          <button className={`q-btn w-full ${kind === "delete" ? "q-btn-danger" : "q-btn-primary"}`} data-testid={`admin-${kind}-submit-btn`}
            disabled={busy || (kind === "delete" ? value !== hub.name : !value)}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {cfg.cta}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProviderNotes() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/provider-status").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <div className="q-card mt-6 space-y-1.5" data-testid="admin-provider-notes">
      <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: `rgb(${VIOLET})` }}>Live data providers</div>
      <p className="text-sm font-semibold text-q-text" data-testid="admin-flight-status-note">{d.flight}</p>
      <p className="text-sm font-semibold text-q-text" data-testid="admin-calls-status-note">{d.calls}</p>
      <p className="text-sm font-semibold text-q-text" data-testid="admin-weather-note">{d.weather}</p>
      <p className="text-xs text-q-muted" data-testid="admin-weather-calls">Weather calls today (UTC): <span className="font-bold tabular-nums text-q-text" data-testid="admin-weather-calls-count">{d.weather_calls_today}</span></p>
    </div>
  );
}

function InboundWarning() {
  const [s, setS] = useState(null);
  const [f, setF] = useState(null);
  useEffect(() => {
    api.get("/admin/inbound-status").then(({ data }) => setS(data)).catch(() => {});
    api.get("/admin/env-flags").then(({ data }) => setF(data)).catch(() => {});
  }, []);
  return <>
    {f?.flags_on?.length > 0 && <p className="mb-4 rounded-xl border border-red-500/50 bg-red-500/10 p-4 text-sm font-semibold text-q-text" role="alert" data-testid="admin-env-flags-warning">
      Test and seed switches are on in this live app: {f.flags_on.join(", ")}. They're ignored here because this isn't the preview, but turn them off (set to false) in the live settings.</p>}
    {s && !s.ok && <p className="mb-6 rounded-xl border border-amber-500/50 bg-amber-500/10 p-4 text-sm font-semibold text-q-text" role="alert" data-testid="admin-inbound-warning">{s.message}</p>}
  </>;
}

export function AdminHome() {
  const nav = useNavigate();
  const { session } = useAuth();
  const [data] = useList("/admin/home");
  if (!data) return <Loader />;
  const preview = () => nav(`/h/${data.demo.organization_id}/home`);
  return (
    <div data-testid="admin-home-page">
      <PageHeader eyebrow="Admin console" title={`Hi ${session.user.name.split(" ")[0]}`} rgb={VIOLET} testid="admin-home-title" />
      <InboundWarning />
      {data.demo && (
        <button onClick={preview} data-testid="admin-preview-demo-btn"
          className="group relative w-full overflow-hidden rounded-3xl border p-8 text-left transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 sm:p-10"
          style={{ borderColor: "rgba(var(--glow-home),0.45)", background: "radial-gradient(circle at 100% 0%, rgba(var(--glow-home),0.22), transparent 60%), rgb(var(--q-surface))", boxShadow: "0 0 50px rgba(var(--glow-home),0.14)" }}>
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-q-accent">Demo hub</div>
          <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-q-text sm:text-4xl" data-testid="admin-preview-demo-title">Preview demo family</h2>
          <p className="mt-2 max-w-lg text-q-muted">Open {data.demo.name} with full access to test every feature, or use the demo toolbar to view as any member, switch Active/Lapsed or reset the demo.</p>
          <span className="q-btn q-btn-primary mt-6 inline-flex"><Eye className="h-4 w-4" /> Open {data.demo.name}</span>
        </button>
      )}
      <ProviderNotes />
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <button className="q-card text-left transition-[border-color] duration-150 hover:border-q-faint" onClick={() => nav("/admin/families")} data-testid="admin-home-families-card">
          <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: `rgb(${VIOLET})` }}>Families</div>
          <div className="mt-2 font-display text-3xl font-extrabold text-q-text">{data.families}</div>
        </button>
        <button className="q-card text-left transition-[border-color] duration-150 hover:border-q-faint" onClick={() => nav("/admin/users")} data-testid="admin-home-users-card">
          <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: `rgb(${VIOLET})` }}>Users</div>
          <div className="mt-2 font-display text-3xl font-extrabold text-q-text">{data.users}</div>
        </button>
      </div>
    </div>
  );
}

export function AdminFamilies() {
  const nav = useNavigate();
  const { session } = useAuth();
  const isSuper = session?.user?.platform_role === "super_admin";
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [tier, setTier] = useState("");
  const qs = [status && `status=${status}`, tier && `plan=${tier}`].filter(Boolean).join("&");
  const [rows, load] = useList(`/admin/families${qs ? `?${qs}` : ""}`, q);
  const [action, setAction] = useState(null);
  const [openError, setOpenError] = useState("");

  const open = async (f) => {
    setOpenError("");
    try {
      await api.post(`/admin/organizations/${f.id}/open`);
      nav(`/h/${f.id}/home`);
    } catch (e) { setOpenError(`${f.name}: ${errMsg(e)}`); }
  };

  return (
    <div data-testid="admin-families-page">
      <PageHeader eyebrow="Admin" title="Families" rgb={VIOLET} testid="admin-families-title" />
      <BillingStats />
      <AiUsage />
      <SearchBox value={q} onChange={setQ} placeholder="Search hub name or owner email" testid="admin-families-search" />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-status-filter">
        {STATUSES.map((s) => <button key={s || "all"} onClick={() => setStatus(s)} aria-pressed={status === s} data-testid={`admin-status-filter-${s || "all"}`}
          className={`min-h-[44px] rounded-full border px-3 text-sm font-semibold capitalize ${status === s ? "border-transparent bg-q-text text-q-bg" : "border-q-border text-q-muted"}`}>{s ? s.replace("_", " ") : "All"}</button>)}
      </div>
      <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-plan-filter">
        {TIERS.map((s) => <button key={s || "all"} onClick={() => setTier(s)} aria-pressed={tier === s} data-testid={`admin-plan-filter-${s || "all"}`}
          className={`min-h-[44px] rounded-full border px-3 text-sm font-semibold ${tier === s ? "border-transparent bg-q-text text-q-bg" : "border-q-border text-q-muted"}`}>{s ? PLAN_NAMES[s] : "All plans"}</button>)}
      </div>
      {openError && <p className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-semibold text-red-300" data-testid="families-open-error">{openError}</p>}
      {!rows ? <Loader /> : rows.length === 0 ? <EmptyState title="No families match" hint="Try another name or email." rgb={VIOLET} /> : (
        <ul className="divide-y divide-q-border overflow-hidden rounded-xl border border-q-border bg-q-surface">
          {rows.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-4 sm:px-5" data-testid={`admin-family-row-${f.id}`}>
              <div className="min-w-[200px] flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-q-text" data-testid="admin-family-name">{f.name}</span>
                  {f.is_demo && <StatusPill label="Demo" rgb={VIOLET} testid={`admin-family-demo-${f.id}`} />}
                  {f.priority && <StatusPill label="Priority" rgb="var(--glow-family)" testid={`admin-family-priority-${f.id}`} />}
                  {f.early_access && <StatusPill label="Early access" rgb="var(--glow-money)" testid={`admin-family-early-${f.id}`} />}
                  {f.founding && f.founding !== "released" && <StatusPill label={`Founding · ${f.founding}`} rgb="var(--glow-kids)" testid={`admin-family-founding-${f.id}`} />}
                  {f.support_access_until && <StatusPill label="Support access" rgb="var(--glow-family)" testid={`admin-family-support-${f.id}`} />}
                </div>
                <div className="mt-1 text-xs text-q-faint" data-testid={`admin-family-meta-${f.id}`}>
                  {f.owner_email} · {f.adults} {f.adults === 1 ? "adult" : "adults"} / {f.kids} {f.kids === 1 ? "kid" : "kids"} · created {fmt(f.created_at)} · <span data-testid={`admin-family-storage-${f.id}`}>{f.storage?.label}</span>
                </div>
              </div>
              <div className="text-right" data-testid={`admin-family-plan-${f.id}`}>
                <div className="mb-1 text-xs font-bold text-q-text" data-testid={`admin-family-tier-${f.id}`}>{f.tier_name}</div>
                <StatusPill label={f.plan.label} rgb={PLAN_RGB[f.plan.status]} />
                {f.plan.ends_at && <div className="mt-1 text-[11px] text-q-faint">{f.plan.status === "active" ? "renews" : "ends"} {fmt(f.plan.ends_at)}</div>}
                {f.stripe_customer_url && <a href={f.stripe_customer_url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center text-[11px] font-semibold text-q-muted underline" data-testid={`admin-family-stripe-link-${f.id}`}>Stripe customer</a>}
              </div>
              <div className="flex flex-wrap gap-1">
                <button className="q-btn q-btn-secondary px-3 py-1.5 text-xs" onClick={() => open(f)} data-testid={`admin-family-open-btn-${f.id}`}><Eye className="h-3.5 w-3.5" /> Open</button>
                <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => setAction({ kind: "extend", hub: f })} data-testid={`admin-family-extend-btn-${f.id}`}><CalendarPlus className="h-3.5 w-3.5" /> Extend</button>
                <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => setAction({ kind: "comp", hub: f })} data-testid={`admin-family-comp-btn-${f.id}`}><Gift className="h-3.5 w-3.5" /> Comp</button>
                {isSuper && <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => setAction({ kind: "storage", hub: f })} data-testid={`admin-family-storage-btn-${f.id}`}>Storage</button>}
                <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs hover:text-red-300" onClick={() => setAction({ kind: "delete", hub: f })} data-testid={`admin-family-delete-btn-${f.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <ActionDialog key={action ? `${action.kind}-${action.hub.id}` : "none"} action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />
    </div>
  );
}

export function AdminUsers() {
  const [q, setQ] = useState("");
  const [rows, load] = useList("/admin/users", q);
  const act = async (u, path, fallback) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/${path}`);
      toast.success(data.message || fallback);
      if (data.warning) toast.warning(data.warning);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-users-page">
      <PageHeader eyebrow="Admin" title="Users" rgb={VIOLET} testid="admin-users-title" />
      <SearchBox value={q} onChange={setQ} placeholder="Search name or email" testid="admin-users-search" />
      {!rows ? <Loader /> : rows.length === 0 ? <EmptyState title="No users match" hint="Try another name or email." rgb={VIOLET} /> : (
        <ul className="divide-y divide-q-border overflow-hidden rounded-xl border border-q-border bg-q-surface">
          {rows.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-4 sm:px-5" data-testid={`admin-user-row-${u.id}`}>
              <div className="min-w-[220px] flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-q-text">{u.name}</span>
                  {u.platform_role && <StatusPill label={u.platform_role === "super_admin" ? "Super admin" : "Admin"} rgb={VIOLET} />}
                </div>
                <div className="text-xs text-q-muted">{u.email}</div>
                <div className="mt-1 text-xs text-q-faint" data-testid={`admin-user-meta-${u.id}`}>
                  {u.methods.join(" + ") || "—"} · {u.hubs.length ? u.hubs.join(", ") : "No hubs"} · joined {fmt(u.created_at)} · last sign-in {fmtTime(u.last_sign_in_at)}
                </div>
              </div>
              <StatusPill label={u.status} rgb={u.status === "Active" ? "var(--glow-money)" : "var(--glow-media)"} testid={`admin-user-status-${u.id}`} />
              <div className="flex flex-wrap gap-1">
                {u.status === "Active" ? (
                  <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs hover:text-red-300" onClick={() => act(u, "disable", "User disabled")} data-testid={`admin-user-disable-btn-${u.id}`}><Ban className="h-3.5 w-3.5" /> Disable</button>
                ) : (
                  <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => act(u, "reactivate", "User reactivated")} data-testid={`admin-user-reactivate-btn-${u.id}`}><RotateCcw className="h-3.5 w-3.5" /> Reactivate</button>
                )}
                <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => act(u, "send-reset", "Reset link sent")} data-testid={`admin-user-reset-btn-${u.id}`}><KeyRound className="h-3.5 w-3.5" /> Send password reset</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AdminAdmins() {
  const { session } = useAuth();
  const [data, load] = useList("/admin/admins");
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const add = async (e) => {
    e.preventDefault();
    setMsg("");
    setError("");
    try {
      const { data } = await api.post("/admin/admins", { email });
      setMsg(data.message);
      setEmail("");
      load();
    } catch (err) { setError(errMsg(err)); }
  };
  const call = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return <Loader />;
  return (
    <div data-testid="admin-admins-page">
      <PageHeader eyebrow="Admin" title="Admins" rgb={VIOLET} testid="admin-admins-title" />
      {data.can_manage && (
        <form onSubmit={add} className="q-card mb-6 max-w-xl space-y-3" data-testid="admin-add-form">
          <Field label="Add an admin by email">
            <div className="flex gap-2">
              <input type="email" className="q-input" placeholder="name@the-queue.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-add-email-input" />
              <button className="q-btn q-btn-primary" disabled={!email} data-testid="admin-add-btn"><UserPlus className="h-4 w-4" /> Add</button>
            </div>
          </Field>
          {msg && <p className="text-sm font-semibold text-cyan-200" data-testid="admin-add-message">{msg}</p>}
          {error && <p className="q-error" data-testid="admin-add-error">{error}</p>}
        </form>
      )}
      <ul className="divide-y divide-q-border overflow-hidden rounded-xl border border-q-border bg-q-surface" data-testid="admin-list">
        {data.admins.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-3.5 sm:px-5" data-testid={`admin-row-${a.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-q-text">{a.name}{a.id === session?.user?.id && <span className="ml-1.5 text-xs text-q-faint">(you)</span>}</div>
              <div className="text-xs text-q-faint">{a.email}</div>
            </div>
            <StatusPill label={a.role === "super_admin" ? "Super admin" : "Admin"} rgb={VIOLET} testid={`admin-role-${a.id}`} />
            {data.can_manage && (
              <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs hover:text-red-300" onClick={() => call(() => api.delete(`/admin/admins/${a.id}`), "Admin removed")} data-testid={`admin-remove-btn-${a.id}`}><X className="h-3.5 w-3.5" /> Remove</button>
            )}
          </li>
        ))}
        {data.pending.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-3.5 sm:px-5" data-testid={`admin-pending-row-${p.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-q-text">{p.email}</div>
              <div className="text-xs text-q-faint">Invited {fmt(p.created_at)} · becomes an admin when they create an account</div>
            </div>
            <StatusPill label="Pending" rgb="var(--glow-calendar)" />
            {data.can_manage && <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => call(() => api.delete(`/admin/admin-invites/${p.id}`), "Invite revoked")} data-testid={`admin-pending-revoke-btn-${p.id}`}><X className="h-3.5 w-3.5" /> Revoke</button>}
          </li>
        ))}
      </ul>
      {!data.can_manage && <p className="mt-4 text-xs text-q-faint" data-testid="admins-readonly-note">Only the super admin can add or remove admins.</p>}
    </div>
  );
}

export function AdminAudit() {
  const [rows] = useList("/admin/audit");
  return (
    <div data-testid="admin-audit-page">
      <PageHeader eyebrow="Admin" title="Audit log" rgb={VIOLET} testid="admin-audit-title" />
      {!rows ? <Loader /> : rows.length === 0 ? <EmptyState title="Nothing logged yet" hint="Admin actions like disabling a user or opening support view show up here." rgb={VIOLET} /> : (
        <ul className="divide-y divide-q-border overflow-hidden rounded-xl border border-q-border bg-q-surface">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3 sm:px-5" data-testid={`audit-row-${r.id}`}>
              <span className="w-44 text-xs tabular-nums text-q-faint">{fmtTime(r.at)}</span>
              <span className="font-semibold text-q-text" data-testid="audit-action">{r.action}</span>
              <span className="text-sm text-q-muted">{r.target_label}</span>
              <span className="ml-auto text-xs text-q-faint">by {r.actor_name}{r.detail ? ` · ${r.detail}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
